import organisationsAPI from '@/api/organisationsAPI.js';
import { ErrorService } from '@/services/error-service.js';

/**************** STATE *******************/
const state = {
  jurisdictionsCount: 0,
  jurisdictionsList: [],
  jurisdictionForm: {
    displayName: null,
    codename: null,
    municipalities: null
  },
  currentJurisdiction: null,

  isJurisdictionsListSearched: false,
  searchJurisdictionsFilter: null,
  jurisdictionsListCurrentPage: 1
};

/**************** GETTERS *****************/
const getters = {
  currentJurisdictionIndex: (state) => {
    if (
      state.currentJurisdiction &&
      state.jurisdictionsList &&
      state.jurisdictionsList.length
    ) {
      return state.jurisdictionsList.findIndex(el => el.id === state.currentJurisdiction.id);
    }
    return false;
  },
  currentJurisdictionBounds: (state) => {
    if (
      state.currentJurisdiction &&
      state.currentJurisdiction.latlon_bounding_box &&
      state.currentJurisdiction.latlon_bounding_box.length === 4
    ) {
      const bounds = state.currentJurisdiction.latlon_bounding_box;
      return [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]]
      ];
    }
    return false;
  }
};

/*************** MUTATIONS ****************/
const mutations = {
  SET_JURIDICTIONS_LIST: (state, payload) => {
    if (payload && payload.count) {
      state.jurisdictionsCount = payload.count;
    }
    if (payload && payload.results && payload.results.length) {
      state.jurisdictionsList = payload.results;
    } else {
      state.jurisdictionsCount = 0;
      state.jurisdictionsList = [];
    }
  },
  SET_JURIDICTION_FORM: (state, payload) => {
    state.jurisdictionForm = payload;
  },
  SET_CURRENT_JURIDICTION: (state, payload) => {
    state.currentJurisdiction = payload;
  },
  SET_IS_JURISDICTIONSLIST_SEARCHED: (state, payload) => {
    state.isJurisdictionsListSearched = payload.isSearched;
    state.searchJurisdictionsFilter = payload.text;
  },
  SET_JURISDICTIONS_LIST_CURRENT_PAGE: (state, payload) => {
    state.jurisdictionsListCurrentPage = payload;
  }
};
/**************** ACTIONS *****************/
const actions = {
  GET_JURISDICTIONS_LIST: async ({ commit }, { page } = { page: 1 }) => {
    const jurisdictionsList = await organisationsAPI.getJurisdictionsList(page);
    commit('SET_JURIDICTIONS_LIST', jurisdictionsList);
  },
  SEARCH_JURISDICTIONS_LIST: async ({ rootState, commit, dispatch }, { text }) => {
    if (text) {
      if (rootState.abortControllers.length > 0) {
        commit('USE_ABORT_CONTROLLER', 'abort_search_jurisdictions', { root: true });
      }
      const controller = new AbortController();
      commit('SET_ABORT_CONTROLLER', {
        id: 'abort_search_jurisdictions',
        controller: controller
      }, { root: true });
  
      try {
        const response = await organisationsAPI.searchJurisdictionsList(text, controller);
        if (response.status === 200) {
          const jurisdictions = response.data;
          if (jurisdictions) {
            commit('SET_JURIDICTIONS_LIST', jurisdictions);
            commit('SET_IS_JURISDICTIONSLIST_SEARCHED', {
              isSearched: true,
              text: text
            });
          }
          commit('REMOVE_ABORT_CONTROLLER', 'abort_search_jurisdictions', { root: true });
          return true;
        }
        return false;
      } catch(err) {
        if (err && err.code && err.code !== 'ERR_CANCELED') {
          ErrorService.onError(err);
          commit('REMOVE_ABORT_CONTROLLER', 'abort_search_jurisdictions', { root: true });
          return true;
        }
        return false;
      }
    } else {
      if (rootState.abortControllers.length > 0) {
        commit('USE_ABORT_CONTROLLER', 'abort_search_jurisdictions', { root: true });
      }
      commit('SET_IS_JURISDICTIONSLIST_SEARCHED', {
        isSearched: false,
        text: null
      });
      await dispatch('GET_JURISDICTIONS_LIST');
      return true;
    }
  },
  CREATE_JURISDICTION: async ({ state, rootState, commit, dispatch }) => {
    const municipalitiesData = rootState.municipalities.selectedMunicipalities.map(el => el.insee);
    const data = {
      display_name: state.jurisdictionForm.displayName,
      codename: state.jurisdictionForm.codename,
      municipalities: municipalitiesData,
    };
    const createdJurisdiction = await organisationsAPI.createJurisdiction(data);
    commit('SET_CURRENT_JURIDICTION', createdJurisdiction);
    await dispatch('GET_JURISDICTIONS_LIST');
  },
  GET_JURISDICTION: async ({ commit }, jurisdictionId) => {
    const jurisdiction = await organisationsAPI.getJurisdiction(jurisdictionId);
    commit('SET_CURRENT_JURIDICTION', jurisdiction);
  },
  UPDATE_JURISDICTION: async ({ rootState, commit }, jurisdictionId) => {
    const municipalitiesData = rootState.municipalities.selectedMunicipalities.map(el => el.insee);
    const data = {
      display_name: state.jurisdictionForm.displayName,
      codename: state.jurisdictionForm.codename,
      municipalities: municipalitiesData,
    };
    const updatedJurisdiction = await organisationsAPI.updateJurisdiction(jurisdictionId, data);
    commit('SET_CURRENT_JURIDICTION', updatedJurisdiction);
  },
  DELETE_JURISDICTION: async ({ dispatch }, jurisdictionId) => {
    await organisationsAPI.deleteJurisdiction(jurisdictionId);
    await dispatch('GET_JURISDICTIONS_LIST');
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
